import axios from 'axios';
import {notify} from '@kyvg/vue3-notification';
import {clearAuthToken, getAuthToken} from '../utils/auth-token';
import i18n from '@/language';

const translate = function (key, params) {
    if (!i18n || !i18n.global || typeof i18n.global.t !== 'function') {
        return key;
    }
    return i18n.global.t(key, params || {});
};

const replaceLaravelPlaceholders = function (text, params) {
    let value = String(text || '');
    Object.keys(params || {}).forEach(function (key) {
        const replacement = String(params[key] ?? '');
        value = value.replace(new RegExp(':' + key + '\\b', 'g'), replacement);
    });
    return value;
};

const translateErrorText = function (text) {
    const value = String(text || '').trim();
    if (!value) {
        return value;
    }

    const applyTemplate = function (key, params) {
        const translated = translate(key, params);
        return replaceLaravelPlaceholders(translated || key, params || {});
    };

    const translatedAttribute = function (attribute) {
        const normalized = String(attribute || '').trim();
        if (!normalized) {
            return normalized;
        }
        return applyTemplate(normalized);
    };

    const validationPatterns = [
        {
            regex: /^The (.+) has already been taken\.?$/i,
            format: function (match) {
                return applyTemplate('The :attribute has already been taken', {
                    attribute: translatedAttribute(match[1]),
                }) + '.';
            },
        },
        {
            regex: /^The (.+) field is required\.?$/i,
            format: function (match) {
                return applyTemplate('The :attribute field is required', {
                    attribute: translatedAttribute(match[1]),
                }) + '.';
            },
        },
        {
            regex: /^The (.+) may not be greater than (\d+) characters\.?$/i,
            format: function (match) {
                return applyTemplate('The :attribute may not be greater than :max characters', {
                    attribute: translatedAttribute(match[1]),
                    max: match[2],
                }) + '.';
            },
        },
        {
            regex: /^The (.+) must be at least (\d+) characters\.?$/i,
            format: function (match) {
                return applyTemplate('The :attribute must be at least :min characters', {
                    attribute: translatedAttribute(match[1]),
                    min: match[2],
                }) + '.';
            },
        },
        {
            regex: /^The (.+) must be an integer\.?$/i,
            format: function (match) {
                return applyTemplate('The :attribute must be an integer', {
                    attribute: translatedAttribute(match[1]),
                }) + '.';
            },
        },
        {
            regex: /^The (.+) must be a valid email address\.?$/i,
            format: function (match) {
                return applyTemplate('The :attribute must be a valid email address', {
                    attribute: translatedAttribute(match[1]),
                }) + '.';
            },
        },
        {
            regex: /^The (.+) format is invalid\.?$/i,
            format: function (match) {
                return applyTemplate('The :attribute format is invalid', {
                    attribute: translatedAttribute(match[1]),
                }) + '.';
            },
        },
        {
            regex: /^The selected (.+) is invalid\.?$/i,
            format: function (match) {
                return applyTemplate('The selected :attribute is invalid', {
                    attribute: translatedAttribute(match[1]),
                }) + '.';
            },
        },
        {
            regex: /^The (.+?)(?: field)? must be a string\.?$/i,
            format: function (match) {
                return applyTemplate('The :attribute must be a string', {
                    attribute: translatedAttribute(match[1]),
                }) + '.';
            },
        },
        {
            regex: /^The (.+?)(?: field)? must be true or false\.?$/i,
            format: function (match) {
                return applyTemplate('The :attribute field must be true or false', {
                    attribute: translatedAttribute(match[1]),
                }) + '.';
            },
        },
        {
            regex: /^The (.+?)(?: field)? must be a number\.?$/i,
            format: function (match) {
                return applyTemplate('The :attribute must be a number', {
                    attribute: translatedAttribute(match[1]),
                }) + '.';
            },
        },
        {
            regex: /^The (.+?)(?: field)? must be between ([+-]?\d+(?:\.\d+)?) and ([+-]?\d+(?:\.\d+)?)\.?$/i,
            format: function (match) {
                return applyTemplate('The :attribute must be between :min and :max', {
                    attribute: translatedAttribute(match[1]),
                    min: match[2],
                    max: match[3],
                }) + '.';
            },
        },
        {
            regex: /^The (.+?)(?: field)? must be at least ([+-]?\d+(?:\.\d+)?)\.?$/i,
            format: function (match) {
                return applyTemplate('The :attribute must be at least :min', {
                    attribute: translatedAttribute(match[1]),
                    min: match[2],
                }) + '.';
            },
        },
        {
            regex: /^The (.+?)(?: field)? must not be greater than ([+-]?\d+(?:\.\d+)?)\.?$/i,
            format: function (match) {
                return applyTemplate('The :attribute must not be greater than :max', {
                    attribute: translatedAttribute(match[1]),
                    max: match[2],
                }) + '.';
            },
        },
        {
            regex: /^The (.+?)(?: field)? must be (\d+) characters\.?$/i,
            format: function (match) {
                return applyTemplate('The :attribute must be :size characters', {
                    attribute: translatedAttribute(match[1]),
                    size: match[2],
                }) + '.';
            },
        },
        {
            regex: /^The (.+?)(?: field)? must be an array\.?$/i,
            format: function (match) {
                return applyTemplate('The :attribute must be an array', {
                    attribute: translatedAttribute(match[1]),
                }) + '.';
            },
        },
        {
            regex: /^The (.+?)(?: field)? must be a valid date\.?$/i,
            format: function (match) {
                return applyTemplate('The :attribute must be a valid date', {
                    attribute: translatedAttribute(match[1]),
                }) + '.';
            },
        },
        {
            regex: /^The (.+?)(?: field)? has a duplicate value\.?$/i,
            format: function (match) {
                return applyTemplate('The :attribute has duplicate values', {
                    attribute: translatedAttribute(match[1]),
                }) + '.';
            },
        },
        {
            regex: /^The (.+?)(?: field)? is prohibited\.?$/i,
            format: function (match) {
                return applyTemplate('The :attribute is prohibited', {
                    attribute: translatedAttribute(match[1]),
                }) + '.';
            },
        },
    ];

    for (let index = 0; index < validationPatterns.length; index += 1) {
        const pattern = validationPatterns[index];
        const match = value.match(pattern.regex);
        if (match) {
            return pattern.format(match);
        }
    }

    const translated = translate(value).toString();
    if (translated !== value) {
        return translated;
    }

    const withoutDot = value.replace(/\.$/, '');
    if (withoutDot !== value) {
        const translatedWithoutDot = translate(withoutDot).toString();
        if (translatedWithoutDot !== withoutDot) {
            return translatedWithoutDot + '.';
        }
    }

    return value;
};

const shouldShowDeveloperNotificationDetails = function () {
    return !!(window.app && window.app.developer_notification_debug_enabled);
};

axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
if (window.app.url) {
    axios.defaults.baseURL = window.app.url;
}
const authToken = getAuthToken();
if (authToken) {
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + authToken;
}
axios.interceptors.response.use(function (response) {
    return response;
}, function (error) {
    if (axios.isCancel(error) || (error && error.code === 'ERR_CANCELED')) {
        return Promise.reject(error);
    }

    const hasResponse = !!(error && error.response);
    const status = hasResponse ? error.response.status : null;
    const requestUrl = error && error.config && error.config.url ? error.config.url : 'n/a';
    const requestMethod = error && error.config && error.config.method ? String(error.config.method).toUpperCase() : 'n/a';

    if (status === 401) {
        // If error 401 redirect to login
        window.location.href = window.app.url + '/auth/login';
        delete window.axios.defaults.headers.common.Authorization;
        clearAuthToken();
        throw new Error(translateErrorText('Unauthorized'));
    } else if (hasResponse && error.response.data && error.response.data.message) {
        // If it is a notification error, display message
        Ladda.stopAll();
        let message = translateErrorText(error.response.data.message);
        if (error.response.data.errors) {
            message = '<ul class="text-left">';
            Object.keys(error.response.data.errors).forEach(function (element) {
                error.response.data.errors[element].forEach(function (item) {
                    message += '<li>' + translateErrorText(item) + '</li>';
                });
            });
            message += '</ul>';
        }
        if (shouldShowDeveloperNotificationDetails()) {
            message += '<hr class="my-2"/><div class="text-left text-xs">';
            message += '<div><strong>' + translateErrorText('Request') + ':</strong> ' + requestMethod + ' ' + requestUrl + '</div>';
            message += '<div><strong>' + translateErrorText('Status') + ':</strong> ' + status + '</div>';
            if (error.response.data.debug) {
                Object.keys(error.response.data.debug).forEach(function (key) {
                    message += '<div><strong>' + key + ':</strong> ' + String(error.response.data.debug[key]) + '</div>';
                });
            }
            message += '</div>';
        }
        const notificationTitle = status === 422
            ? translateErrorText('Please check your entries')
            : translateErrorText('Error');
        notify({title: notificationTitle, text: message, type: 'error', duration: -1});
        throw new Error(translateErrorText(error.response.data.message));
    } else {
        // If it is an uncontrolled error, display http status
        Ladda.stopAll();
        let text = translateErrorText(hasResponse ? error.response.statusText : 'Network error');
        if (shouldShowDeveloperNotificationDetails()) {
            text += '<hr class="my-2"/><div class="text-left text-xs">';
            text += '<div><strong>' + translateErrorText('Request') + ':</strong> ' + requestMethod + ' ' + requestUrl + '</div>';
            text += '<div><strong>' + translateErrorText('Status') + ':</strong> ' + (status || 'n/a') + '</div>';
            text += '</div>';
        }
        notify({title: translateErrorText('Error') + ' ' + (status || ''), text: text, type: 'error', duration: -1});
        throw new Error(translateErrorText(error && error.message ? error.message : 'Network error'));
    }
});

window.axios = axios;
